import React from 'react'
import { object } from 'yup'
import styled from '@emotion/styled'
import { useTranslation } from 'next-i18next'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import ModalPanel from 'modules/modal/components/modal-panel/modal-panel'
import useModal from 'modules/modal/state/use-modal'
import useOrder from 'modules/order/state/order/use-order'
import useFinalizeSingleStepOrder from 'modules/order/finalize-order/use-finalize-single-step-order'
import InputField from 'modules/ui/molecules/FormComponents/InputField'
import SelectField from 'modules/ui/molecules/FormComponents/SelectField'
import Row from 'modules/ui/atoms/Layout/Row'
import Column from 'modules/ui/atoms/Layout/Column'
import { STRIPE_COUNTRY_CODES } from 'utils/constants/misc'

const closeButtonDataId = 'missing-digital-payment-info-fallback-modal-close-button'

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

interface MissingPaymentInfoFormData {
  firstName: string
  lastName: string
  email: string
  country: string
  zipCode: string
}

const MissingDigitalPaymentInfoFallbackModalPanel = () => {
  const { t } = useTranslation()
  const modal = useModal()
  const order = useOrder()
  const finalizeSingleStepOrder = useFinalizeSingleStepOrder()

  // Set up form with current order data
  const form = useForm<MissingPaymentInfoFormData>({
    resolver: yupResolver(
      object({
        firstName: order.contact.schema.fields.firstName,
        lastName: order.contact.schema.fields.lastName,
        email: order.contact.schema.fields.email,
        country: order.address.schema.fields.country,
        zipCode: order.address.schema.fields.zipCode,
      }),
    ),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: order.contact.value.firstName || '',
      lastName: order.contact.value.lastName || '',
      email: order.contact.value.email || '',
      country: order.address.value.country || 'US',
      zipCode: order.address.value.zipCode || '',
    },
  })

  const watchCountry = form.watch('country')

  // Convert country codes to options for the select field
  const countryOptions = STRIPE_COUNTRY_CODES.map((country) => ({
    value: country.code,
    label: country.country,
  }))

  // Helper function to cast error type
  const fieldError = (fieldName: keyof MissingPaymentInfoFormData) =>
    form.formState.errors[fieldName] as { message: string } | undefined

  const onSubmit = async (data: MissingPaymentInfoFormData) => {
    try {
      // Update order contact information
      order.contact.setValue({
        ...order.contact.value,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
      })

      // Update order address information
      order.address.setValue({
        ...order.address.value,
        country: data.country,
        zipCode: data.zipCode,
      })

      // Close the modal first
      modal.close()

      // Finalize the order
      await finalizeSingleStepOrder()
    } catch (error) {
      console.error('Error finalizing order:', error)
      // Optionally handle error state here
    }
  }

  if (!modal.current) {
    return null
  }

  return (
    <ModalPanel title={modal.current.label} closeButtonDataId={closeButtonDataId}>
      <ModalPanel.Body>
        <div>
          <p>
            {t(
              'modal.missingDigitalPaymentInfo.description',
              "To complete your donation, we'll need the following information:",
            )}
          </p>

          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <FormContainer>
                <Row>
                  <Column>
                    <InputField
                      required
                      id="firstName"
                      label={t('modal.missingDigitalPaymentInfo.firstName', 'First name')}
                      error={fieldError('firstName')}
                      autoComplete="given-name"
                      dataId="missing-payment-info-first-name"
                    />
                  </Column>
                  <Column>
                    <InputField
                      required
                      id="lastName"
                      label={t('modal.missingDigitalPaymentInfo.lastName', 'Last name')}
                      error={fieldError('lastName')}
                      autoComplete="family-name"
                      dataId="missing-payment-info-last-name"
                    />
                  </Column>
                </Row>

                <InputField
                  required
                  id="email"
                  label={t('modal.missingDigitalPaymentInfo.email', 'Email')}
                  type="email"
                  error={fieldError('email')}
                  autoComplete="email"
                  dataId="missing-payment-info-email"
                />

                <Row>
                  <Column>
                    <SelectField
                      required
                      id="country"
                      label={t('modal.missingDigitalPaymentInfo.country', 'Country')}
                      error={fieldError('country')}
                      options={countryOptions}
                      autoComplete="country"
                      dataId="missing-payment-info-country"
                      value={watchCountry}
                    />
                  </Column>
                  <Column>
                    <InputField
                      required
                      id="zipCode"
                      label={
                        watchCountry === 'US'
                          ? t('modal.missingDigitalPaymentInfo.zipCode', 'Zip code')
                          : t('modal.missingDigitalPaymentInfo.postalCode', 'Postal code')
                      }
                      error={fieldError('zipCode')}
                      autoComplete="postal-code"
                      dataId="missing-payment-info-zip-code"
                    />
                  </Column>
                </Row>
              </FormContainer>
            </form>
          </FormProvider>
        </div>
      </ModalPanel.Body>
      <ModalPanel.Footer>
        <ModalPanel.FooterAction onClick={form.handleSubmit(onSubmit)}>
          {t('modal.missingDigitalPaymentInfo.submit', 'Complete donation')}
        </ModalPanel.FooterAction>
      </ModalPanel.Footer>
    </ModalPanel>
  )
}

export default MissingDigitalPaymentInfoFallbackModalPanel
